import * as Yup from 'yup';

import {
  NE_DOCUMENTS_FIELDS,
  NE_EXPERTS_DOCUMENTS_FIELDS,
} from 'components/NEForm/Utils/DocumentsFields';
import { getInstallerEntryByExpert } from 'components/NEForm/Utils/NEDataUtils';

import { FORM_CONSTANTS, VALIDATION_CONSTANTS } from 'global/constants';
import { evaluateCondition } from 'global/fieldValidationFunction';
import { FIRST_QUALIFICATIONS, SECOND_QUALIFICATIONS } from 'global/qualifications';

import { isRegisteringInstallerOnlyHilfsbetrieb } from 'utils/InstallerUtils';
import { getDefaultPflegendeBUForCurrentTenant, getTenantId } from 'utils/tenantUtil';

export const calculateExpertTriggers = (experts, branchSelectionValues) => {
  const expertTriggers = {};
  experts.forEach((expert) => {
    const expertQualificationsTriggers = [];
    if (expert.firstQualificationEnergy) {
      expertQualificationsTriggers.push(
        FIRST_QUALIFICATIONS[expert.firstQualificationEnergy].triggerID
      );
    }
    if (expert.firstQualificationGas) {
      expertQualificationsTriggers.push(
        FIRST_QUALIFICATIONS[expert.firstQualificationGas].triggerID
      );
    }
    if (expert.secondQualificationEnergy) {
      expertQualificationsTriggers.push(
        SECOND_QUALIFICATIONS[expert.secondQualificationEnergy].triggerID
      );
    }
    if (expert.secondQualificationGas) {
      expertQualificationsTriggers.push(
        SECOND_QUALIFICATIONS[expert.secondQualificationGas].triggerID
      );
    }
    if (expert.isExpertOwner) {
      expertQualificationsTriggers.push(40);
    }
    expert.expertQualificationsTriggers = expertQualificationsTriggers;
    const evaluateConditionsConfig = {
      branchSelectionValues,
      isOnlyHilfsbetrieb: isRegisteringInstallerOnlyHilfsbetrieb(branchSelectionValues),
      form: FORM_CONSTANTS.NE,
      expertQualificationsTriggers,
      expert,
    };
    const triggers = NE_EXPERTS_DOCUMENTS_FIELDS[
      getInstallerEntryByExpert(expert, branchSelectionValues)
    ][getTenantId()]
      .filter((field) =>
        field.whenShouldFieldRender
          ? evaluateCondition(field.whenShouldFieldRender, evaluateConditionsConfig)
          : true
      )
      .map((field) => field.id); // TODO: refactor
    expertTriggers[expert.id] = triggers;
  });
  return expertTriggers;
};

export const calculateDocumentTriggers = (branchSelectionValues, installerEntry) => {
  const evaluateConditionsConfig = {
    branchSelectionValues,
    isOnlyHilfsbetrieb: isRegisteringInstallerOnlyHilfsbetrieb(branchSelectionValues),
    form: FORM_CONSTANTS.NE,
  };

  const triggers = NE_DOCUMENTS_FIELDS[installerEntry][getTenantId()]
    .filter((field) =>
      field.whenShouldFieldRender
        ? evaluateCondition(field.whenShouldFieldRender, evaluateConditionsConfig)
        : true
    )
    .map((field) => field.id);

  return triggers;
};

export const createInitialValues = (experts, documentTriggerIds, expertTriggers) => {
  const initialValues = {
    experts,
    documentsTrigger: documentTriggerIds,
    expertTrigger: expertTriggers,
    isAddressPublic: false,
    canSendMail: false,
    changes: '',
    pflegendeBU: getDefaultPflegendeBUForCurrentTenant(),
  };
  return initialValues;
};

export const createValidationSchema = (
  initialValues,
  expertTriggers,
  experts,
  branchSelectionValues,
  installerEntry
) => {
  const validationObject = {};

  let evaluateConditionsConfig = {
    branchSelectionValues,
    isOnlyHilfsbetrieb: isRegisteringInstallerOnlyHilfsbetrieb(branchSelectionValues),
    form: FORM_CONSTANTS.NE,
  };
  NE_DOCUMENTS_FIELDS[installerEntry][getTenantId()]
    .filter((section) =>
      section.whenShouldFieldRender
        ? evaluateCondition(section.whenShouldFieldRender, evaluateConditionsConfig)
        : true
    )
    .forEach((section) => {
      validationObject[section.name] = Yup.array().when([], {
        is: () =>
          evaluateCondition(section.isOptional, {
            isOnlyHilfsbetrieb: isRegisteringInstallerOnlyHilfsbetrieb(branchSelectionValues),
            form: FORM_CONSTANTS.NE,
          }),
        then: Yup.array(),
        otherwise: Yup.array().min(1).required(VALIDATION_CONSTANTS.MANDATORY),
      });

      initialValues[section.name] = [];
    });

  // eslint-disable-next-line array-callback-return
  Object.keys(expertTriggers).map((key) => {
    let expertValidation = {};
    const expert = experts.find((expertFind) => expertFind.id === key);
    const expertQualificationsTriggers = [];
    if (expert.firstQualificationEnergy) {
      expertQualificationsTriggers.push(
        FIRST_QUALIFICATIONS[expert.firstQualificationEnergy].triggerID
      );
    }
    if (expert.firstQualificationGas) {
      expertQualificationsTriggers.push(
        FIRST_QUALIFICATIONS[expert.firstQualificationGas].triggerID
      );
    }
    if (expert.secondQualificationEnergy) {
      expertQualificationsTriggers.push(
        SECOND_QUALIFICATIONS[expert.secondQualificationEnergy].triggerID
      );
    }
    if (expert.secondQualificationGas) {
      expertQualificationsTriggers.push(
        SECOND_QUALIFICATIONS[expert.secondQualificationGas].triggerID
      );
    }
    if (expert.isExpertOwner) {
      expertQualificationsTriggers.push(40);
    }
    evaluateConditionsConfig = {
      branchSelectionValues,
      isOnlyHilfsbetrieb: isRegisteringInstallerOnlyHilfsbetrieb(branchSelectionValues),
      form: FORM_CONSTANTS.NE,
      expertQualificationsTriggers,
      expert,
    };
    initialValues[key] = {};

    NE_EXPERTS_DOCUMENTS_FIELDS[getInstallerEntryByExpert(expert, branchSelectionValues)][
      getTenantId()
    ]
      .filter((field) =>
        field.whenShouldFieldRender
          ? evaluateCondition(field.whenShouldFieldRender, evaluateConditionsConfig)
          : true
      )
      .forEach((section) => {
        expertValidation = {
          ...expertValidation,
          [section.name]: Yup.array().when([], {
            is: () =>
              section.isOptional
                ? evaluateCondition(section.isOptional, { evaluateConditionsConfig })
                : true,
            then: Yup.array(),
            otherwise: Yup.array().min(1).required(VALIDATION_CONSTANTS.MANDATORY),
          }),
        };
        initialValues[key][section.name] = [];
      });
    validationObject[key] = Yup.object().shape(expertValidation);
  });

  const validationSchema = Yup.object().shape({
    changes: Yup.string()
      .trim()
      .nullable()
      .typeError('must be a string')
      .transform((value) => (value === '' ? null : value))
      .min(2, VALIDATION_CONSTANTS.TOO_SHORT)
      .max(FORM_CONSTANTS.FREETEXT_INPUT, VALIDATION_CONSTANTS.TOO_LONG),
    isAddressPublic: Yup.bool().required(),
    canSendMail: Yup.bool().required(),
    pflegendeBU: Yup.string()
      .trim()
      .nullable()
      .typeError('must be a string')
      .transform((value) => (value === '' ? null : value)),
    ...validationObject,
  });

  return validationSchema;
};
